import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { useQuery } from '@tanstack/react-query';

import { Box, Flex, NativeSelect, Text } from '@chakra-ui/react';

import { companyQueries } from '@/features/companies/api/companyQueries';
import { jobQueries, type JobStatus } from '@/features/jobs/api/jobQueries';
import { JobTable } from '@/features/jobs/components/JobTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const jobsSearchSchema = z.object({
  page: z.number().int().positive().catch(1),
  limit: z.number().int().min(1).max(100).catch(20),
  sortBy: z.enum(['title', 'createdAt']).optional().catch(undefined),
  sortOrder: z.enum(['asc', 'desc']).optional().catch(undefined),
  query: z.string().max(100).optional().catch(undefined),
  status: z.enum(['DRAFT', 'PUBLISHED', 'CLOSED']).optional().catch(undefined),
  companyId: z.number().optional().catch(undefined),
});

const STATUS_OPTIONS: { value: JobStatus; label: string }[] = [
  { value: 'DRAFT', label: '下書き' },
  { value: 'PUBLISHED', label: '公開中' },
  { value: 'CLOSED', label: '終了' },
];

export const Route = createFileRoute('/_authenticated/jobs/')({
  validateSearch: zodValidator(jobsSearchSchema),
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(jobQueries.list(deps.search)),
  component: JobsPage,
});

function JobsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: companyData } = useQuery(companyQueries.list({ page: 1, pageSize: 100 }));

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">
          求人管理
        </Text>
        <Button colorPalette="brand" onClick={() => navigate({ to: '/jobs/new' })}>
          新規作成
        </Button>
      </Flex>

      <Flex gap={4} mb={4} wrap="wrap">
        <Box minW="40">
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={search.status ?? ''}
              onChange={(e) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    status: e.target.value === '' ? undefined : (e.target.value as JobStatus),
                    page: 1,
                  }),
                })
              }
            >
              <option value="">すべてのステータス</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>

        <Box minW="48">
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={search.companyId?.toString() ?? ''}
              onChange={(e) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    companyId: e.target.value === '' ? undefined : Number(e.target.value),
                    page: 1,
                  }),
                })
              }
            >
              <option value="">すべての企業</option>
              {companyData?.items.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>

        <Box minW="60" flex={1} maxW="80">
          <Input
            placeholder="キーワード検索"
            value={search.query ?? ''}
            onChange={(e) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  query: e.target.value === '' ? undefined : e.target.value,
                  page: 1,
                }),
              })
            }
          />
        </Box>
      </Flex>

      <JobTable
        search={search}
        onSearchChange={(partial) => navigate({ search: (prev) => ({ ...prev, ...partial }) })}
        onRowClick={(job) => navigate({ to: '/jobs/$jobId', params: { jobId: job.id } })}
      />
    </Box>
  );
}
